"use client"

import * as React from "react"
import { create } from "zustand"

import { X } from "lucide-react"

import { SlotMachine } from "@/components/slot/slot-machine"
import { Button } from "@/components/ui/button"
import { useLitReels } from "@/lib/slot/use-lit-reels"

/* The drums opened from a reward, over whatever screen the reader is on.
   Same room, entrance and exit as the cards' takeover: the board under a
   dark tint with the floor's lamp and bloom, no slip of its own (the
   board's real panel is raised above the cover and takes the line), and
   once the lineup has landed the room lets go slowly. */

interface TakeoverState {
  open: boolean
  show: () => void
  hide: () => void
}

export const useSlotTakeover = create<TakeoverState>()((set) => ({
  open: false,
  show: () => set({ open: true }),
  hide: () => set({ open: false }),
}))

export const selectSlotTakeoverOpen = (s: TakeoverState) => s.open
export const selectShowSlotTakeover = (s: TakeoverState) => s.show
export const selectHideSlotTakeover = (s: TakeoverState) => s.hide

const OUT_MS = 520
const APPLIED_OUT_MS = 1600

function Ground() {
  return (
    <>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(60% 45% at 50% 0%, hsl(var(--primary) / 0.22) 0%, transparent 70%)" }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1.4px)",
          backgroundSize: "7px 7px",
          opacity: 0.1,
          mixBlendMode: "overlay",
          maskImage: "linear-gradient(to bottom, black 0%, transparent 70%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 70%)",
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(90% 70% at 4% 100%, hsl(var(--primary) / 0.14) 0%, transparent 55%)" }}
      />
    </>
  )
}

function Table({ onApplied }: { onApplied: () => void }) {
  const { reels, ready } = useLitReels()
  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center px-4 py-6">
      <Ground />
      <SlotMachine reels={reels} ready={ready} onApplied={onApplied} />
    </main>
  )
}

export function SlotTakeover() {
  const open = useSlotTakeover(selectSlotTakeoverOpen)
  const hide = useSlotTakeover(selectHideSlotTakeover)
  const [mounted, setMounted] = React.useState(open)
  const [slow, setSlow] = React.useState(false)
  React.useEffect(() => {
    if (open) {
      setMounted(true)
      setSlow(false)
      return
    }
    const t = window.setTimeout(() => setMounted(false), slow ? APPLIED_OUT_MS : OUT_MS)
    return () => window.clearTimeout(t)
  }, [open, slow])
  const applied = React.useCallback(() => {
    setSlow(true)
    hide()
  }, [hide])
  React.useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") hide()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open, hide])
  if (!mounted) return null
  return (
    <div
      className={
        "fixed inset-0 z-[300] overflow-y-auto bg-black/75 " +
        (open ? "wheel-takeover-in" : slow ? "cards-takeover-out" : "wheel-takeover-out")
      }
    >
      <div className={open ? "wheel-takeover-floor-in" : ""}>
        <Table onApplied={applied} />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Close"
        onClick={hide}
        className="absolute right-4 top-4 z-[311] text-grey-400 hover:text-white"
      >
        <X />
      </Button>
    </div>
  )
}
